import React from "react";
import moment from "moment-timezone";

import { DatePicker, Form, Input, Modal, message } from "antd";

import { _postTermContract, _putTermContract } from "../../services";
import { EnterpriseSelect } from "./selects";
import { TIMEZONE, errorMessage, toDay } from "./helpers";

const toState = ({
    contract,
    enterpriseName,
}) => contract ? {
    enterpriseId: `${contract.enterpriseId}`,
    reference: contract.reference || "",
    title: contract.title || "",
    notes: contract.notes || "",
    dates: [moment.tz(contract.startDate, TIMEZONE), moment.tz(contract.endDate, TIMEZONE)],
    enterpriseName
} : { enterpriseId: null, reference: "", title: "", notes: "", dates: [], enterpriseName: null };

/** Create (no `contract`) or edit a term contract header. */
class TermContractHeaderModal extends React.Component {

    state = { ...toState({ contract: this.props.contract, enterpriseName: this.props.enterpriseName }), saving: false };

    save = async () => {
        const { enterpriseId, reference, title, notes, dates } = this.state;
        if (!enterpriseId || !reference.trim() || dates.length !== 2) {
            message.error("Enterprise, reference and start/end dates are required.");
            return;
        }

        const body = {
            enterpriseId,
            reference: reference.trim(),
            title: title.trim(),
            notes: notes.trim(),
            startDate: dates[0].format("YYYY-MM-DD"),
            endDate: dates[1].format("YYYY-MM-DD")
        };

        this.setState({ saving: true });
        try {
            const { contract } = this.props;
            const { data } = contract ? await _putTermContract({ id: contract._id, data: body }) : await _postTermContract({ data: body });
            message.success(contract ? "Term contract updated" : "Term contract created");
            this.props.onSaved(data.termContract);
        } catch (e) {
            message.error(errorMessage({ error: e }));
            this.setState({ saving: false });
        }
    };

    render() {
        const { contract, onCancel } = this.props;
        const { enterpriseId, enterpriseName, reference, title, notes, dates, saving } = this.state;
        return (
            <Modal
                visible
                title={contract ? `Edit ${contract.reference}` : "New term contract"}
                okText="Save"
                confirmLoading={saving}
                onOk={this.save}
                onCancel={onCancel}
            >
                <Form layout="vertical">
                    <Form.Item label="Enterprise" required
                        help={contract ? "Changing the enterprise re-checks every line for overlaps." : null}>
                        <EnterpriseSelect
                            value={enterpriseId}
                            initialOption={enterpriseId && enterpriseName ? { value: enterpriseId, label: enterpriseName } : null}
                            onChange={(value, option) => this.setState({ enterpriseId: value, enterpriseName: option && option.label })}
                        />
                    </Form.Item>
                    <Form.Item label="Reference (tender / contract no.)" required>
                        <Input value={reference} onChange={e => this.setState({ reference: e.target.value })} />
                    </Form.Item>
                    <Form.Item label="Title">
                        <Input value={title} onChange={e => this.setState({ title: e.target.value })} />
                    </Form.Item>
                    <Form.Item label="Start and end date (whole Singapore days; set an earlier end date to terminate early)" required>
                        <DatePicker.RangePicker
                            value={dates}
                            format="YYYY-MM-DD"
                            onChange={value => this.setState({ dates: value || [] })}
                        />
                    </Form.Item>
                    <Form.Item label="Notes">
                        <Input.TextArea rows={3} value={notes} onChange={e => this.setState({ notes: e.target.value })} />
                    </Form.Item>
                </Form>
            </Modal>
        );
    }
}

/** "Extend / end early": only the end date. An earlier end date terminates the contract early. */
export class EndDateModal extends React.Component {

    state = { endDate: moment.tz(this.props.contract.endDate, TIMEZONE), saving: false };

    save = async () => {
        const { contract, onSaved } = this.props;
        const { endDate } = this.state;
        if (!endDate) {
            message.error("Pick an end date.");
            return;
        }

        this.setState({ saving: true });
        try {
            await _putTermContract({ id: contract._id, data: { endDate: endDate.format("YYYY-MM-DD") } });
            message.success("End date updated");
            onSaved();
        } catch (e) {
            message.error(errorMessage({ error: e }));
            this.setState({ saving: false });
        }
    };

    render() {
        const { contract, onCancel } = this.props;
        const { endDate, saving } = this.state;
        return (
            <Modal visible title="Extend / end early" okText="Save" confirmLoading={saving} onOk={this.save} onCancel={onCancel}>
                <p>
                    Currently {toDay({ date: contract.startDate })} to {toDay({ date: contract.endDate })} (whole Singapore days).
                    A later date extends the contract; an earlier one ends it early, and its prices stop applying after that day.
                </p>
                <Form layout="vertical">
                    <Form.Item label="End date" required>
                        <DatePicker value={endDate} format="YYYY-MM-DD" allowClear={false} onChange={value => this.setState({ endDate: value })} />
                    </Form.Item>
                </Form>
            </Modal>
        );
    }
}

export default TermContractHeaderModal;



// WEBPACK FOOTER //
// ./src/components/term-contract/header-modal.js